import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import logger from "@/lib/logger";

export default function ShiftConfigTab() {
  const [shiftConfig, setShiftConfig] = useState([]);
  const { user } = useAuth();

  const fetchShiftConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/shift-config");
      setShiftConfig(data.shifts || []);
    } catch (err) { logger.error("Failed to fetch shift config:", err); }
  }, []);

  useEffect(() => { fetchShiftConfig(); }, [fetchShiftConfig]);

  const handleSaveShifts = async () => {
    try {
      await api.put("/admin/shift-config", { shifts: shiftConfig });
      toast.success("Shift config saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <Card className="mt-4 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold" style={{ fontFamily: "Chivo" }}>Shift Configuration</h3>
        {user?.role === "admin" && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShiftConfig(prev => [...prev, { name: "", start: "", end: "" }])} data-testid="add-shift-btn">
              <Plus size={14} className="mr-1" /> Add Shift
            </Button>
            <Button size="sm" className="h-8 text-xs bg-[#1B3C87]" onClick={handleSaveShifts} data-testid="save-shifts-btn">Save</Button>
          </div>
        )}
      </div>
      <div className="space-y-4">
        {shiftConfig.map((shift, i) => (
          <div key={shift.name || `shift-${i}`} className="flex items-center gap-4 p-4 border rounded">
            <div className="flex items-center gap-2 flex-1">
              <Label className="text-xs w-14">Name:</Label>
              <Input value={shift.name} onChange={e => { const s = [...shiftConfig]; s[i].name = e.target.value; setShiftConfig(s); }} className="w-32 h-9" disabled={user?.role !== "admin"} />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Start:</Label>
              <Input type="time" value={shift.start} onChange={e => { const s = [...shiftConfig]; s[i].start = e.target.value; setShiftConfig(s); }} className="w-32 h-9" disabled={user?.role !== "admin"} />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">End:</Label>
              <Input type="time" value={shift.end} onChange={e => { const s = [...shiftConfig]; s[i].end = e.target.value; setShiftConfig(s); }} className="w-32 h-9" disabled={user?.role !== "admin"} />
            </div>
            {user?.role === "admin" && shiftConfig.length > 1 && (
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => setShiftConfig(prev => prev.filter((_, j) => j !== i))}>
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        ))}
        {shiftConfig.length === 0 && <p className="text-sm text-gray-400 py-4">No shifts configured. Add one above.</p>}
      </div>
      <div className="mt-4 p-3 bg-blue-50 rounded text-sm text-blue-700">
        Shift times are used for alert triggers and handover prompts. Changes apply immediately after saving.
      </div>
    </Card>
  );
}
